import React from 'react';

import { Navigate } from 'react-router';

import { FullscreenLoader } from '@hrlogics/react-components';

import { appRoutes } from '@clearverify/constants';

import { CompanyType } from 'src/api';
import { SignupConsumerFormStepId } from 'src/constants/auth';
import { useAuthSignupConsumer } from 'src/hooks/auth';
import { useInviteParamId } from 'src/hooks/invite';
import {
  useEmployeeBasicInfoFormHandler,
  usePinFormSubmitHandler,
  useSsnFormSubmitHandler,
} from 'src/hooks/signup';
import { SxProp } from 'src/types/mui';

import { AuthPaperBox } from '../AuthPaperBox';

import {
  ConsumerAttestationsForm,
  ConsumerBasicInfoForm,
  ConsumerReviewDataForm,
  ConsumerVerificationPreferenceForm,
  PinForm,
  SsnForm,
} from './components';

export const SignupConsumerPageForm: React.FC = () => {
  const inviteId = useInviteParamId();

  const {
    employerInfo,
    updateFormData,
    goToNextStep,
    loading,
    employeeSignupToken,
    setEmployeeSignupToken,
    formStepperState,
  } = useAuthSignupConsumer();

  const boxStyles: SxProp | undefined = React.useMemo(() => {
    if (formStepperState.currentStepId === SignupConsumerFormStepId.consumerReviewData) {
      return consumerReviewDataContainerStyles;
    }
    return undefined;
  }, [formStepperState.currentStepId]);

  const { handleSsnFormSubmit } = useSsnFormSubmitHandler({
    companyId: employerInfo?.id || '',
    companyType: CompanyType.Employer,
    updateFormData,
    setEmployeeSignupToken,
    goToNextStep,
  });
  const { handlePinFormSubmit } = usePinFormSubmitHandler({
    companyId: employerInfo?.id || '',
    companyType: CompanyType.Employer,
    updateFormData,
    setEmployeeSignupToken,
    goToNextStep,
  });
  const { handleEmployeeBasicInfoFormSubmit } = useEmployeeBasicInfoFormHandler({
    inviteId,
    employeeSignupToken,
    updateFormData,
    setEmployeeSignupToken,
    goToNextStep,
  });

  const renderForm = () => {
    switch (formStepperState.currentStepId) {
      case SignupConsumerFormStepId.ssn:
        return <SsnForm onSubmit={handleSsnFormSubmit} companyName={employerInfo?.displayName} />;
      case SignupConsumerFormStepId.pin:
        return (
          <PinForm
            onSubmit={handlePinFormSubmit}
            companyName={employerInfo?.displayName}
            customHint={employerInfo?.pinHint}
          />
        );
      case SignupConsumerFormStepId.consumerBasicInfo:
        return (
          <ConsumerBasicInfoForm
            onSubmit={handleEmployeeBasicInfoFormSubmit}
            signUpMethod={employerInfo?.signUpMethod}
            displayName={employerInfo?.displayName}
            loading={loading}
          />
        );
      case SignupConsumerFormStepId.consumerAttestations:
        return <ConsumerAttestationsForm />;
      case SignupConsumerFormStepId.consumerReviewData:
        return <ConsumerReviewDataForm />;
      case SignupConsumerFormStepId.consumerVerificationPreference:
        return <ConsumerVerificationPreferenceForm />;
      default:
        return null;
    }
  };

  if (loading) {
    return <FullscreenLoader sx={{ height: 'unset' }} />;
  }

  if (!employerInfo) {
    return <Navigate to={appRoutes.auth.index} />;
  }

  return (
    <AuthPaperBox
      sx={boxStyles}
      variant={employerInfo.logo?.downloadUrl ? 'withCompanyLogo' : 'default'}
      companyLogoUrl={employerInfo.logo?.downloadUrl}
    >
      {renderForm()}
    </AuthPaperBox>
  );
};

const consumerReviewDataContainerStyles: SxProp = {
  maxWidth: 'none',
  minWidth: 540,
};
